import type { VendorApiOperationKey } from '../../types/vendor.types';

export type VendorApiOperationGroupKey = 'sites' | 'chargers' | 'sessions' | 'reports';

export interface VendorApiOperationDefinition {
    key: VendorApiOperationKey;
    method: 'GET' | 'POST';
    path: string;
    labelKey: string;
}

export interface VendorApiOperationGroup {
    key: VendorApiOperationGroupKey;
    operations: VendorApiOperationDefinition[];
}

const operation = (
    key: VendorApiOperationKey,
    method: 'GET' | 'POST',
    path: string,
    label: string,
): VendorApiOperationDefinition => ({
    key,
    method,
    path,
    labelKey: `resources.vendors.apiAccess.operations.${label}`,
});

export const VENDOR_API_OPERATION_GROUPS: VendorApiOperationGroup[] = [
    { key: 'sites', operations: [operation('sites.list', 'GET', '/v1/sites', 'sitesList')] },
    {
        key: 'chargers',
        operations: [
            operation('chargers.list', 'GET', '/v1/chargers', 'chargersList'),
            operation('chargers.get', 'GET', '/v1/chargers/:id', 'chargersGet'),
        ],
    },
    {
        key: 'sessions',
        operations: [
            operation('sessions.start', 'POST', '/v1/sessions/start', 'sessionsStart'),
            operation('sessions.stop', 'POST', '/v1/sessions/stop', 'sessionsStop'),
            operation('sessions.list', 'GET', '/v1/sessions', 'sessionsList'),
            operation('sessions.get', 'GET', '/v1/sessions/:id', 'sessionsGet'),
            operation('sessions.meter_values', 'GET', '/v1/sessions/:id/meter-values', 'sessionsMeterValues'),
        ],
    },
    {
        key: 'reports',
        operations: [
            operation('reports.sessions', 'GET', '/v1/reports/sessions', 'reportsSessions'),
            operation('reports.sessions_export', 'GET', '/v1/reports/sessions/export', 'reportsSessionsExport'),
        ],
    },
];

export const ALL_VENDOR_API_OPERATION_KEYS: VendorApiOperationKey[] = VENDOR_API_OPERATION_GROUPS.flatMap((group) =>
    group.operations.map((entry) => entry.key),
);

export const VENDOR_API_OPERATION_COUNT = ALL_VENDOR_API_OPERATION_KEYS.length;

export const VENDOR_API_LIMIT_BOUNDS = {
    control: { min: 1, max: 10000, default: 100 },
    read: { min: 1, max: 100000, default: 1000 },
} as const;

const isOperationKey = (value: unknown): value is VendorApiOperationKey =>
    typeof value === 'string' && (ALL_VENDOR_API_OPERATION_KEYS as string[]).includes(value);

export function normalizeSelection(value: unknown): VendorApiOperationKey[] {
    if (!Array.isArray(value)) {
        throw new Error(`apiAllowedOperations is not an array: ${JSON.stringify(value)}`);
    }
    for (const entry of value) {
        if (!isOperationKey(entry)) {
            throw new Error(`unknown API operation ${JSON.stringify(entry)}`);
        }
    }
    const wanted = new Set<VendorApiOperationKey>(value);
    return ALL_VENDOR_API_OPERATION_KEYS.filter((key) => wanted.has(key));
}

export function countEnabled(value: unknown): number {
    return normalizeSelection(value).length;
}

function groupKeys(group: VendorApiOperationGroupKey): VendorApiOperationKey[] {
    const found = VENDOR_API_OPERATION_GROUPS.find((entry) => entry.key === group);
    if (!found) {
        throw new Error(`unknown API operation group ${group}`);
    }
    return found.operations.map((entry) => entry.key);
}

export function withGroup(selected: VendorApiOperationKey[], group: VendorApiOperationGroupKey): VendorApiOperationKey[] {
    return normalizeSelection([...selected, ...groupKeys(group)]);
}

export function withoutGroup(selected: VendorApiOperationKey[], group: VendorApiOperationGroupKey): VendorApiOperationKey[] {
    const removed = new Set(groupKeys(group));
    return normalizeSelection(selected.filter((key) => !removed.has(key)));
}

export function toggleOperation(selected: VendorApiOperationKey[], key: VendorApiOperationKey): VendorApiOperationKey[] {
    return selected.includes(key)
        ? normalizeSelection(selected.filter((entry) => entry !== key))
        : normalizeSelection([...selected, key]);
}

export function wholeNumber(message: string = 'resources.vendors.validation.integer') {
    return (value: unknown): string | undefined => {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }
        return Number.isInteger(Number(value)) ? undefined : message;
    };
}
